import React, { useState, useEffect } from 'react';
import UserLayout from '../components/UserLayout';
import { apiClient } from '../api/api';
import Loader from '../components/Loader';
import './PaidAdsPage.css';
import { useAuth } from '../context/AuthContext';
import { 
  TrendingUp, 
  TrendingDown,
  Search,
  Filter,
  Download,
  Activity,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const PaidAdsPage = () => {
  const [data, setData] = useState({ campaigns: [], summary: {}, trends: [] });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const { selectedShopId, dateRange } = useAuth();

  useEffect(() => {
    fetchPaidAdsMetrics();
  }, [selectedShopId, dateRange]);

  const fetchPaidAdsMetrics = async () => {
    try {
      setLoading(true);
      let url = `/paid-ads-metrics?range=${dateRange}`;
      if (selectedShopId) {
        url += `&shop_id=${selectedShopId}`;
      }
      const response = await apiClient.get(url);
      setData(response.data);
    } catch (error) {
      console.error('Error fetching paid ads metrics:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (num) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(num);
  };

  const parseCurrency = (val) => {
    if (typeof val === 'number') return val;
    if (typeof val === 'string' && val !== '--') return parseFloat(val.replace(/[^0-9.-]+/g,""));
    return 0;
  };

  const sanitizeText = (text) => {
    if (!text) return "";
    // Remove leading/trailing commas, periods, or spaces and ensure first char is capitalize
    let clean = text.trim().replace(/^[,.\s]+|[,.\s]+$/g, "");
    return clean.charAt(0).toUpperCase() + clean.slice(1);
  };

  const renderInsight = (ins, i) => (
    <div key={i} className="recap-insight">
      <div className="insight-emoji">{ins.emoji}</div>
      <div className="insight-content">
        {ins.prefix && <div className="insight-prefix" style={{ color: ins.color }}>{sanitizeText(ins.prefix)}</div>}
        <div className="insight-text-wrapper">
          {sanitizeText(ins.text)}{' '}
          {ins.highlight && (
            <span className="insight-highlight" style={{ color: ins.highlight_color || ins.color }}>
              {ins.highlight}
            </span>
          )}
        </div>
      </div>
    </div>
  );

  const chartData = {
    labels: (data.trends || []).map(t => t.date),
    datasets: [
      {
        label: 'Ad Spend',
        data: (data.trends || []).map(t => t.spend),
        borderColor: '#3b82f6',
        backgroundColor: (context) => {
          const ctx = context.chart.ctx;
          const gradient = ctx.createLinearGradient(0, 0, 0, 300);
          gradient.addColorStop(0, 'rgba(59, 130, 246, 0.1)');
          gradient.addColorStop(1, 'rgba(59, 130, 246, 0)');
          return gradient;
        },
        fill: true,
        tension: 0.4,
        pointRadius: 0,
        borderWidth: 2,
      },
      {
        label: 'Revenue',
        data: (data.trends || []).map(t => t.revenue),
        borderColor: '#10b981',
        backgroundColor: (context) => {
          const ctx = context.chart.ctx;
          const gradient = ctx.createLinearGradient(0, 0, 0, 300);
          gradient.addColorStop(0, 'rgba(16, 185, 129, 0.25)');
          gradient.addColorStop(1, 'rgba(16, 185, 129, 0)');
          return gradient;
        },
        fill: true,
        tension: 0.4,
        pointRadius: 0,
        borderWidth: 2,
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        backgroundColor: '#111',
        titleColor: '#888',
        bodyColor: '#fff',
        borderColor: '#333',
        borderWidth: 1,
        padding: 12,
        cornerRadius: 8,
      }
    },
    hover: {
      mode: 'index',
      intersect: false
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: '#555',
          font: {
            size: 11,
            weight: '500'
          },
          maxRotation: 0,
          autoSkip: true,
          maxTicksLimit: 7
        },
        border: {
          display: false
        }
      },
      y: {
        grid: {
          color: 'rgba(255, 255, 255, 0.03)',
          drawBorder: false,
        },
        ticks: {
          color: '#555',
          font: {
            size: 11,
            weight: '500'
          },
          stepSize: 200, // Matching screenshot increments if possible
          callback: (val) => val.toLocaleString()
        },
        border: {
          display: false,
          dash: [4, 4]
        }
      }
    }
  };



  return (
    <UserLayout>
      <div className="page-relative-wrapper">
        <div className="paid-ads-container">
        {/* Redesigned AI Campaign Insights Widget */}
        <div className={`ai-recap-card ${loading ? 'shimmer skeleton' : ''}`}>
          <div className="recap-header">
            <div className="recap-header-left">
              <div className="recap-icon-box">
                <div className="inner-icon-bg">
                  <Activity size={24} color="#3b82f6" />
                </div>
              </div>
              <div>
                <div className="recap-title">{data?.ai_recap?.title || 'AI Campaign Insights'}</div>
                <div className="recap-subtitle">{data?.ai_recap?.subtitle || 'Last 30 Days Performance Analysis'}</div>
              </div>
            </div>
            <div className="status-badge">{data?.ai_recap?.status_badge || 'Active'}</div>
          </div>
          <div className="recap-body">
            {(() => {
              const insights = data?.ai_recap?.insights || [];
              
              if (insights.length === 0 && data.campaigns && data.campaigns.length > 0) {
                const sortedByRoas = [...data.campaigns].sort((a, b) => (b.roas || 0) - (a.roas || 0));
                const topCamp = sortedByRoas[0];
                const avgRoi = data.summary?.roi || 0;
                const revGrowth = data.summary?.revenue_growth || '0%';
                
                const dynamicInsights = [
                  {
                    emoji: '🎯',
                    prefix: 'Top Performer',
                    text: `Your campaign "${topCamp.name}" is leading with a ROAS of`,
                    highlight: `${(topCamp.roas || 0).toFixed(2)}x`,
                    color: '#10b981',
                    highlight_color: '#10b981'
                  },
                  {
                    emoji: '📈',
                    prefix: 'Scaling',
                    text: `Revenue has grown by`,
                    highlight: revGrowth,
                    color: revGrowth.includes('-') ? '#ef4444' : '#10b981',
                    highlight_color: revGrowth.includes('-') ? '#ef4444' : '#10b981'
                  },
                  {
                    emoji: '⚡',
                    prefix: 'Efficiency',
                    text: `Overall ROI is steady at`,
                    highlight: `${avgRoi.toFixed(2)}x`,
                    color: avgRoi >= 2 ? '#3b82f6' : '#f59e0b',
                    highlight_color: avgRoi >= 2 ? '#3b82f6' : '#f59e0b'
                  }
                ];

                if (parseCurrency(data.summary?.cost_per_order || 0) > 0) {
                  dynamicInsights.push({
                    emoji: '💸',
                    prefix: 'Cost Check',
                    text: `Average Cost per Order is`,
                    highlight: formatCurrency(data.summary?.cost_per_order),
                    color: '#a855f7',
                    highlight_color: '#a855f7'
                  });
                }

                return dynamicInsights.map((ins, i) => renderInsight(ins, i));
              }

              return insights.map((ins, i) => renderInsight(ins, i));
            })()}
          </div>
        </div>

        {/* Metric Cards */}
        <div className="metric-grid">
          <div className={`metric-card blue ${loading ? 'shimmer skeleton' : ''}`}>
            <div className="card-label">
              <div className="label-dot" style={{ backgroundColor: '#3b82f6' }} /> Ad Spend
            </div>
            <div className="card-value">{formatCurrency(data.summary?.ad_spend || 0)}</div>
            <div className="trend-container">
              <div className={`trend-badge ${data.summary?.ad_spend_growth?.includes('-') ? 'down' : 'up'}`}>
                {data.summary?.ad_spend_growth?.includes('-') ? <TrendingDown size={14} /> : <TrendingUp size={14} />} 
                {data.summary?.ad_spend_growth?.replace('+','').replace('-','') || '0%'}
              </div>
              <div className="trend-label">vs last period</div>
            </div>
          </div>
          <div className={`metric-card orange ${loading ? 'shimmer skeleton' : ''}`}>
            <div className="card-label">
              <div className="label-dot" style={{ backgroundColor: '#f97316' }} /> ROI
            </div>
            <div className="card-value">{(data.summary?.roi || 0).toFixed(2)}x</div>
            <div className="trend-container">
              <div className={`trend-badge ${data.summary?.roi_growth?.includes('-') ? 'down' : 'up'}`}>
                {data.summary?.roi_growth?.includes('-') ? <TrendingDown size={14} /> : <TrendingUp size={14} />} 
                {data.summary?.roi_growth?.replace('+','').replace('-','') || '0%'}
              </div>
              <div className="trend-label">vs last period</div>
            </div>
          </div>
          <div className={`metric-card purple ${loading ? 'shimmer skeleton' : ''}`}>
            <div className="card-label">
              <div className="label-dot" style={{ backgroundColor: '#a855f7' }} /> Cost per Order
            </div>
            <div className="card-value">{formatCurrency(data.summary?.cost_per_order || 0)}</div>
            <div className="trend-container">
              <div className={`trend-badge ${data.summary?.cpo_growth?.includes('+') ? 'up' : 'down'}`}>
                {data.summary?.cpo_growth?.includes('+') ? <TrendingUp size={14} /> : <TrendingDown size={14} />} 
                {data.summary?.cpo_growth?.replace('+','').replace('-','') || '0%'}
              </div>
              <div className="trend-label">vs last period</div>
            </div>
          </div>
          <div className={`metric-card green ${loading ? 'shimmer skeleton' : ''}`}>
            <div className="card-label">
              <div className="label-dot" style={{ backgroundColor: '#10b981' }} /> Revenue
            </div>
            <div className="card-value">{formatCurrency(data.summary?.revenue || 0)}</div>
            <div className="trend-container">
              <div className={`trend-badge ${data.summary?.revenue_growth?.includes('-') ? 'down' : 'up'}`}>
                {data.summary?.revenue_growth?.includes('-') ? <TrendingDown size={14} /> : <TrendingUp size={14} />} 
                {data.summary?.revenue_growth?.replace('+','').replace('-','') || '0%'}
              </div>
              <div className="trend-label">vs last period</div>
            </div>
          </div>
        </div>

        {/* Trends Chart */}
        <div className={`chart-section ${loading ? 'shimmer skeleton' : ''}`}>
          <div className="chart-header">
            <div>
              <div className="chart-title">Ad Performance Trends</div>
              <div className="chart-subtitle">Ad Spend vs Revenue Over <span className="highlight-text">Last 7 Days</span></div>
            </div>
            <button className="export-btn">
              <Download size={16} /> Export
            </button>
          </div>
          <div style={{ height: '300px', position: 'relative' }}>
             <Line data={chartData} options={chartOptions} />
          </div>
        </div>

        {/* Redesigned Active Campaigns Widget */}
        <div className={`campaigns-section ${loading ? 'shimmer skeleton' : ''}`}>
          <div className="section-header">
            <h2 className="section-title">Active Campaigns</h2>
            <p className="section-subtitle">Manage and monitor your advertising campaigns</p>
          </div>

          <div className="controls-row">
            <div className="search-container">
              <Search className="search-icon" size={20} />
              <input 
                type="text" 
                placeholder="Search campaigns..." 
                className="search-input"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="action-buttons">
              <button className="icon-btn">
                <Filter size={20} />
              </button>
              <button className="icon-btn">
                <ArrowUpDown size={20} />
              </button>
            </div>
          </div>

          <table className="campaign-table">
            <thead>
              <tr>
                <th style={{ width: '25%' }}>Campaign Name</th>
                <th style={{ width: '15%' }}>Schedule</th>
                <th style={{ width: '12%' }}>Status</th>
                <th style={{ width: '12%' }}>Spend</th>
                <th style={{ width: '12%' }}>ROAS</th>
                <th style={{ width: '12%' }}>Conversions</th>
                <th style={{ width: '12%' }}>GMV</th>
              </tr>
            </thead>
            <tbody>
              {(data.campaigns || [])
                .filter(c => c.name.toLowerCase().includes(searchTerm.toLowerCase()))
                .map((campaign, idx) => {
                  const statusClass = campaign.status?.toLowerCase() === 'active' ? 'active' : 'review';
                  const roasTrend = idx % 2 === 0 ? 'up' : 'down'; // Mocking trend for design
                  
                  return (
                    <tr key={idx}>
                      <td>
                        <div className="camp-main-name">{campaign.name}</div>
                        <div className="camp-sub-text">Ads</div>
                      </td>
                      <td>
                        <div className="camp-main-name" style={{ fontWeight: 400, fontSize: '14px' }}>{campaign.schedule}</div>
                        <div className="camp-sub-text">30 days remaining</div>
                      </td>
                      <td>
                        <div className={`status-pill ${statusClass}`}>
                          <div className={`dot ${statusClass}`} />
                          {campaign.status}
                        </div>
                      </td>
                      <td>
                        <div className="spend-amount">{formatCurrency(campaign.spend)}</div>
                        <div className="camp-sub-text">Daily: {formatCurrency(campaign.daily_spend)}</div>
                      </td>
                      <td>
                        <div className="roas-amount">{(campaign.roas || 0).toFixed(2)}x</div>
                        <div className={`trend-${roasTrend}`}>
                          {roasTrend === 'up' ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                          {idx % 2 === 0 ? '24%' : '8%'}
                        </div>
                      </td>
                      <td>
                        <div className="conv-count">{campaign.conversions}</div>
                        <div className="camp-sub-text">CVR: {campaign.cvr}</div>
                      </td>
                      <td>
                        <div className="gmv-value">{formatCurrency(campaign.gmv)}</div>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
          
          <div className="pagination-row">
            <div>Showing {data.campaigns?.length || 0} of {data.campaigns?.length || 0} campaigns</div>
            <div className="pagination-controls">
              <button className="page-btn arrow"><ChevronLeft size={18} /></button>
              <button className="page-btn active">1</button>
              <button className="page-btn">2</button>
              <button className="page-btn">3</button>
              <button className="page-btn arrow"><ChevronRight size={18} /></button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </UserLayout>
);
};

export default PaidAdsPage;
